import { activityUsesDedicatedRoom, activityUsesSprintRoom } from './activityUsesDedicatedRoom'

// Single source of truth for the Whereby "scope" decision: given the resolved activity context, decide
// whether the call is an ACTIVITY-scoped room (games + AI-coach short-reflection — their own per-activity
// room) or a shared TEAM room, and which team id owns that scope. The introduction and the group reflection
// are a SPRINT call held on its own page, so these surfaces host nothing for them. Used by every call surface
// (useWherebyRoom + DashboardCall + DashboardCallFacilitator) so the rule can never drift between them.
//
// NOTE: the route gate (a page may only host its own activity's call) and the call pin (stay on the game
// room while the schedule drifts) live in useWherebyRoom — they are page-specific. This helper only takes
// the gate's verdict as `roomUnavailableForRoute` and never re-implements it.

export interface WherebyScopeInput {
  sprintId?: string
  activityId?: string
  activityType?: string | null
  // The team that owns the activity / hosts the call (already resolved: override ?? pin ?? live).
  teamId?: string
  // True when the current page cannot host this activity's call (route gate verdict). Default false.
  roomUnavailableForRoute?: boolean
}

export interface WherebyScope {
  isDedicatedRoomActivity: boolean
  isActivityScope: boolean
  // The activity is a call of the whole sprint, joined on its own page and hosted by none of these surfaces.
  isSprintScope: boolean
  // Team id for an activity-scoped room (undefined unless isActivityScope).
  activityTeamId?: string
  // Team id for a shared team room (undefined when activity- or sprint-scoped, or route-gated).
  teamRoomTeamId?: string
  // The single team id for the active scope (activity or team). Undefined => there is no call.
  scopeTeamId?: string
  hasScope: boolean
}

export const resolveWherebyScope = (input: WherebyScopeInput): WherebyScope => {
  const { sprintId, activityId, activityType, teamId, roomUnavailableForRoute = false } = input
  const canHostActivityRoom = !roomUnavailableForRoute && Boolean(sprintId && activityId)

  // A game or debrief has a room per team, the introduction and group reflection one per sprint, the rest the team room.
  const isDedicatedRoomActivity = canHostActivityRoom && activityUsesDedicatedRoom(activityType ?? undefined)
  const isSprintRoomActivity = canHostActivityRoom && activityUsesSprintRoom(activityType ?? undefined)

  const activityTeamId = isDedicatedRoomActivity ? teamId : undefined
  const isActivityScope = isDedicatedRoomActivity && Boolean(teamId)
  const isSprintScope = isSprintRoomActivity && Boolean(teamId)
  // An activity room NEVER falls back to a plain team room (that's how a game lands in the wrong shared
  // room) — it waits for its own activity team. A route-gated page hosts no team room either.
  const hasOwnRoom = isDedicatedRoomActivity || isSprintRoomActivity
  const teamRoomTeamId = roomUnavailableForRoute || hasOwnRoom ? undefined : teamId
  const scopeTeamId = isActivityScope ? activityTeamId : teamRoomTeamId

  return {
    isDedicatedRoomActivity,
    isActivityScope,
    isSprintScope,
    activityTeamId,
    teamRoomTeamId,
    scopeTeamId,
    hasScope: Boolean(scopeTeamId)
  }
}
